import { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import Cookies from 'js-cookie'

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  })
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    const cookieConsent = Cookies.get('cookie-consent')
    if (!cookieConsent) {
      setIsVisible(true)
    }
  }, [])

  const acceptAll = () => {
    const consent = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }

    Cookies.set('cookie-consent', JSON.stringify(consent), {
      expires: 365,
      sameSite: 'Strict'
    })

    setIsVisible(false)

    if (consent.analytics) {
      console.log('Analytics enabled')
    }
  }

  const acceptSelected = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    }

    Cookies.set('cookie-consent', JSON.stringify(consent), {
      expires: 365,
      sameSite: 'Strict'
    })

    setIsVisible(false)

    if (preferences.analytics) {
      console.log('Analytics enabled')
    }
  }

  const rejectAll = () => {
    const consent = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }

    Cookies.set('cookie-consent', JSON.stringify(consent), {
      expires: 365,
      sameSite: 'Strict'
    })

    setIsVisible(false)
  }

  const handlePreferenceChange = (type) => {
    if (type === 'essential') return

    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  if (!isVisible) return null

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <motion.div
            className="absolute inset-0 bg-dark-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-hidden="true"
          />

          <motion.div
            initial={{ scale: 0.97, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.98, opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-2xl bg-white dark:bg-dark-800 rounded-2xl shadow-elevated border border-dark-200/60 dark:border-dark-700 p-6 sm:p-8"
          >
            <div className="flex items-start gap-4">
              <div className="flex-1 space-y-3">
                <h3 id="cookie-banner-title" className="text-lg font-bold text-dark-900 dark:text-white">
                  Wir respektieren Ihre Privatsphäre
                </h3>
                <p id="cookie-banner-description" className="text-sm text-dark-500 dark:text-dark-400 leading-relaxed">
                  Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
                  Einige sind technisch notwendig, andere helfen uns, unsere Website zu verbessern und Ihnen
                  relevante Inhalte zu zeigen.
                </p>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-2 space-y-2"
                    >
                      <div className="flex items-center justify-between p-3 bg-dark-50 dark:bg-dark-700/50 rounded-xl">
                        <div>
                          <h4 className="font-medium text-dark-900 dark:text-white text-sm">Notwendige Cookies</h4>
                          <p className="text-xs text-dark-500 dark:text-dark-400">
                            Für die Grundfunktionen der Website erforderlich
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={preferences.essential}
                          disabled
                          className="w-4 h-4 text-primary-600 bg-dark-100 border-dark-300 rounded focus:ring-primary-500 disabled:opacity-50"
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-dark-50 dark:bg-dark-700/50 rounded-xl">
                        <div>
                          <h4 className="font-medium text-dark-900 dark:text-white text-sm">Analyse Cookies</h4>
                          <p className="text-xs text-dark-500 dark:text-dark-400">
                            Helfen uns, die Website zu verbessern
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={() => handlePreferenceChange('analytics')}
                          className="w-4 h-4 text-primary-600 bg-dark-100 border-dark-300 rounded focus:ring-primary-500 cursor-pointer"
                        />
                      </div>

                      <div className="flex items-center justify-between p-3 bg-dark-50 dark:bg-dark-700/50 rounded-xl">
                        <div>
                          <h4 className="font-medium text-dark-900 dark:text-white text-sm">Marketing Cookies</h4>
                          <p className="text-xs text-dark-500 dark:text-dark-400">
                            Für personalisierte Werbung und Inhalte
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={preferences.marketing}
                          onChange={() => handlePreferenceChange('marketing')}
                          className="w-4 h-4 text-primary-600 bg-dark-100 border-dark-300 rounded focus:ring-primary-500 cursor-pointer"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button
                onClick={() => setIsVisible(false)}
                className="p-1.5 text-dark-400 hover:text-dark-600 dark:text-dark-500 dark:hover:text-dark-300 transition-colors duration-150 self-start cursor-pointer"
                aria-label="Cookie-Banner schließen"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-150 cursor-pointer"
              >
                {showDetails ? 'Weniger anzeigen' : 'Mehr erfahren'}
              </button>

              <button
                onClick={rejectAll}
                className="px-4 py-2 text-sm font-medium text-dark-600 dark:text-dark-300 bg-dark-100 dark:bg-dark-700 hover:bg-dark-200 dark:hover:bg-dark-600 rounded-xl transition-colors duration-150 cursor-pointer"
              >
                Alle ablehnen
              </button>

              {showDetails && (
                <button
                  onClick={acceptSelected}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors duration-150 cursor-pointer"
                >
                  Auswahl bestätigen
                </button>
              )}

              <div className="sm:ml-auto">
                <button
                  onClick={acceptAll}
                  className="w-full sm:w-auto btn-primary"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CookieBanner
